import type {
  AppConfig,
  AssetsManifest,
  ContentConfig,
  EconomyConfig,
  GearContentConfig,
  VisualsConfig,
} from './types';
import assetsJson from '../../config/assets.json';
import economyJson from '../../config/economy.json';
import visualsJson from '../../config/visuals.json';
import gearJson from '../../config/content/gear.json';

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const key of Object.keys(value as object)) {
      deepFreeze((value as Record<string, unknown>)[key]);
    }
  }
  return value;
}

function requireNumber(obj: Record<string, unknown>, path: string, key: string): void {
  const v = obj[key];
  if (typeof v !== 'number' || Number.isNaN(v)) {
    throw new Error(`economy.json missing number at ${path}.${key}`);
  }
}

/** Validate and freeze an arbitrary economy object (for tests / sensitivity). */
export function loadEconomy(raw: unknown): EconomyConfig {
  if (!raw || typeof raw !== 'object') {
    throw new Error('economy config must be an object');
  }
  const e = raw as Record<string, unknown>;
  for (const section of [
    'shafts',
    'elevator',
    'warehouse',
    'managers',
    'migration',
    'transportScaling',
    'hero',
    'gear',
    'goldenVeins',
    'guardians',
    'expeditions',
    'numberFormat',
    'milestones',
    'boosts',
  ]) {
    if (!e[section] || typeof e[section] !== 'object') {
      throw new Error(`economy.json missing section: ${section}`);
    }
  }
  const shafts = e.shafts as Record<string, unknown>;
  for (const k of [
    'baseProd',
    'prodGrowth',
    'baseLevelCost',
    'levelCostGrowth',
    'levelCostRate',
    'unlockBase',
    'unlockGrowth',
  ]) {
    requireNumber(shafts, 'shafts', k);
  }
  const milestones = e.milestones as { tiers?: unknown };
  if (!Array.isArray(milestones.tiers) || milestones.tiers.length === 0) {
    throw new Error('economy.json milestones.tiers must be a non-empty array');
  }
  return deepFreeze(structuredClone(raw) as EconomyConfig);
}

export function loadVisuals(raw: unknown): VisualsConfig {
  if (!raw || typeof raw !== 'object') {
    throw new Error('visuals config must be an object');
  }
  const v = raw as Record<string, unknown>;
  for (const station of ['shaft', 'elevator', 'warehouse']) {
    const s = v[station] as { breakpoints?: unknown } | undefined;
    if (!s || !Array.isArray(s.breakpoints) || s.breakpoints.length === 0) {
      throw new Error(`visuals.json ${station}.breakpoints must be a non-empty array`);
    }
  }
  return deepFreeze(structuredClone(raw) as VisualsConfig);
}

export function loadAssets(raw: unknown): AssetsManifest {
  const assets = raw as AssetsManifest;
  if (!assets?.assets?.length) {
    throw new Error('assets.json missing or empty');
  }
  for (const a of assets.assets) {
    if (!a.key || !a.path || (a.kind !== 'image' && a.kind !== 'audio')) {
      throw new Error(`assets.json entry invalid: ${JSON.stringify(a?.key)}`);
    }
  }
  return deepFreeze(structuredClone(raw) as AssetsManifest);
}

export function loadGearContent(raw: unknown): GearContentConfig {
  if (!raw || typeof raw !== 'object') {
    throw new Error('gear content must be an object');
  }
  const g = raw as GearContentConfig;
  if (typeof g.setBonus?.powerBonus !== 'number') {
    throw new Error('gear.json setBonus.powerBonus required');
  }
  return deepFreeze(structuredClone(raw) as GearContentConfig);
}

/** Load and freeze all game config from bundled JSON. */
export function loadConfig(): AppConfig {
  const assets = loadAssets(assetsJson);
  const economy = loadEconomy(economyJson);
  const visuals = loadVisuals(visualsJson);
  const gear = loadGearContent(gearJson);
  const content: ContentConfig = deepFreeze({ gear });
  return deepFreeze({ assets, economy, visuals, content });
}

/** Convenience: clone the bundled economy JSON for mutation in tests. */
export function cloneEconomyJson(): EconomyConfig {
  return structuredClone(economyJson) as EconomyConfig;
}
